import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { Operadora, UserRole } from '../types'
import { validarOperadoraAtiva } from '../services/operadoras'

const STORAGE_KEY = 'agroschelive_operadora'
const AUTH_TIMEOUT_MS = 6000

interface AuthState {
  session: Session | null
  user: User | null
  operadora: Operadora | null
  papel: UserRole | null
  loading: boolean
}

interface AuthActions {
  setOperadoraAtiva: (op: Operadora, papel: UserRole) => void
  clearOperadora: () => void
  signOut: () => Promise<void>
}

type AuthContextType = AuthState & AuthActions

const AuthContext = createContext<AuthContextType | null>(null)

function withTimeout<T>(promise: Promise<T>, timeout = AUTH_TIMEOUT_MS): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error('Tempo limite excedido ao restaurar sessão.')), timeout)
    }),
  ])
}

function limparEstadoLocal() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignora erros de storage.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [operadora, setOperadora] = useState<Operadora | null>(null)
  const [papel, setPapel] = useState<UserRole | null>(null)
  const [loading, setLoading] = useState(true)

  const resetAuthState = useCallback(() => {
    limparEstadoLocal()
    setSession(null)
    setUser(null)
    setOperadora(null)
    setPapel(null)
  }, [])

  const validarOperadoraSalva = useCallback(async (currentUserId: string, savedValue: string | null) => {
    if (!savedValue) return

    try {
      const parsed = JSON.parse(savedValue) as { operadora?: Operadora | Operadora[] | null; papel?: UserRole | null }
      const op = Array.isArray(parsed?.operadora) ? parsed.operadora[0] : parsed?.operadora
      const operadoraId = op?.id

      if (!operadoraId) return

      const validacao = await withTimeout(validarOperadoraAtiva(currentUserId, operadoraId))

      if (!validacao) {
        localStorage.removeItem(STORAGE_KEY)
        return
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(validacao))
      setOperadora(validacao.operadora)
      setPapel(validacao.papel)
    } catch {
      localStorage.removeItem(STORAGE_KEY)
    }
  }, [])

  useEffect(() => {
    let alive = true

    async function iniciarAuth() {
      try {
        const { data: { session } } = await withTimeout(supabase.auth.getSession())

        if (!alive) return

        setSession(session)
        setUser(session?.user ?? null)

        // libera a aplicação imediatamente
        setLoading(false)

        if (!session) {
          setOperadora(null)
          setPapel(null)
          return
        }

        // valida operadora em background
        validarOperadoraSalva(session.user.id, localStorage.getItem(STORAGE_KEY))
      } catch (err) {
        console.error('[AgroSchelive] Falha ao iniciar autenticação:', err)

        if (!alive) return

        resetAuthState()
        setLoading(false)
      }
    }

    iniciarAuth()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_ev, session) => {
      if (!alive) return

      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)

      if (!session) {
        setOperadora(null)
        setPapel(null)
        return
      }

      validarOperadoraSalva(session.user.id, localStorage.getItem(STORAGE_KEY))
    })

    return () => {
      alive = false
      subscription.unsubscribe()
    }
  }, [resetAuthState, validarOperadoraSalva])

  const setOperadoraAtiva = useCallback((op: Operadora, p: UserRole) => {
    setOperadora(op)
    setPapel(p)
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ operadora: op, papel: p }))
  }, [])

  const clearOperadora = useCallback(() => {
    setOperadora(null)
    setPapel(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    resetAuthState()
  }, [resetAuthState])

  return (
    <AuthContext.Provider
      value={{ session, user, operadora, papel, loading, setOperadoraAtiva, clearOperadora, signOut }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>')
  return ctx
}
